import { useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import jsQR from "jsqr";

export function QrScanner({ onDetect, paused, onCameraError }) {
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraError, setCameraError] = useState(false);

  useEffect(() => {
    if (paused || cameraError) return;
    const MAX_DIMENSION = 700;
    const interval = setInterval(() => {
      const video = webcamRef.current?.video;
      if (!video || video.readyState !== 4 || !video.videoWidth) return;
      const scale = Math.min(1, MAX_DIMENSION / Math.max(video.videoWidth, video.videoHeight));
      const canvas = canvasRef.current;
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });
      if (code?.data) onDetect(code.data);
    }, 350);
    return () => clearInterval(interval);
  }, [paused, cameraError, onDetect]);

  const handleCameraError = () => {
    setCameraError(true);
    onCameraError?.();
  };

  if (cameraError) {
    return (
      <div className="aspect-square w-full max-w-sm mx-auto flex items-center justify-center bg-slate-50 border-4 border-slate-200 rounded-lg" data-testid="qr-scanner-camera-error">
        <p className="text-slate-400 text-sm p-6 text-center">Camera unavailable — use manual search instead</p>
      </div>
    );
  }

  return (
    <div className="relative aspect-square w-full max-w-sm mx-auto overflow-hidden rounded-lg border-4 border-brand-navy" data-testid="qr-scanner-viewport">
      <Webcam
        ref={webcamRef}
        audio={false}
        videoConstraints={{ facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } }}
        className="w-full h-full object-cover"
        onUserMediaError={handleCameraError}
        data-testid="qr-scanner-webcam"
      />
      <div className="absolute inset-8 border-2 border-brand-cyan/70 rounded-lg pointer-events-none" />
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
